




































































































































































































import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import { Dictionary } from "@/lib/Dictionary.type";
import AuthenticatedUserProvider from "@/modules/account/authenticated-user-provider.service";
import { Resource } from "@/modules/account/resource";
import IdGenerator from "@/lib/services/id-generator";
import ErrorConverterService from "@/modules/forms/error-converter.service";
import Product from "@/modules/plushie/product.model";
import PlushieStore from "@/modules/plushie/store";
import dataStore from "@/store";

import PercentageInput from "./PercentageInput.vue";

import AllocationStatus from "../../allocation-workspace/AllocationStatus.vue";
import AllocationWorkspace from "../../allocation-workspace/AllocationWorkspace.vue";
import { protectAllocationDraft } from "../../allocation-workspace/protect-allocation-draft";
import {
  AllocationOrientation,
  AllocationDetailStatus,
  createProductAllocationBaseline,
  isValidAllocationPercent,
  materializeProductAllocations,
  productAllocationKey,
  SparseAllocationDraft,
} from "../../allocation-workspace/allocation-state";
import Factory from "../../factory.model";
import FactoryPhaseSupport from "../../factory-phase-support.model";
import { ProductAllocationSlotTypeValue } from "../../product-allication-slot-type.value";
import FactoryStore from "../../store";

interface ProductAllocationSummary {
  total: number;
  hasInvalidPercentage: boolean;
}

const invalidPercentageMessage =
  "Each allocation percentage must be a whole number from 0 to 100.";

function isValidProductTotal(total: number): boolean {
  return total === 0 || total === 100;
}

@Component({
  components: {
    AllocationStatus,
    AllocationWorkspace,
    FormErrors,
    LoadingSpinner,
    PercentageInput,
    SubmitButton,
  },
})
export default class ProductAllocationWorkspace extends Vue {
  @Prop({ required: true })
  public readonly slotType!: ProductAllocationSlotTypeValue;

  @Prop({ default: false }) public readonly editable!: boolean;
  @Inject("IdGenerator") private fIdGenerator!: IdGenerator;
  @Inject("ErrorConverterService")
  private fErrorConverterService!: ErrorConverterService;
  @Inject("AuthenticatedUserProvider")
  private fUserProvider!: AuthenticatedUserProvider;

  private fFactoryDataStore: FactoryStore;
  private fPlushieDataStore: PlushieStore;
  private fState = new SparseAllocationDraft<number>({}, 0);
  private fIsLoading = true;
  private fIsSaving = false;
  private fIsSaved = false;
  private fShowMobileValidation = false;
  private fLoadErrors: string[] = [];
  private fSaveErrors: string[] = [];
  private fRemoveDraftGuard = (): void => undefined;
  private fSupportStatus: "unknown" | "loading" | "available" | "permission" | "error" = "unknown";
  private fSupportErrors: string[] = [];
  private fSupportGeneration = 0;
  private fSupportKey = "";
  private fSupportSnapshot: FactoryPhaseSupport[] = [];
  private fDestroyed = false;
  private fInputResetToken = 0;
  private fPendingInputs: Dictionary<string> = {};

  constructor() {
    super();
    this.fFactoryDataStore = getModule(FactoryStore, dataStore);
    this.fPlushieDataStore = getModule(PlushieStore, dataStore);
  }

  get factories(): Factory[] {
    return this.fFactoryDataStore.activeFactoriesList;
  }

  get visibleFactories(): Factory[] {
    if (this.supportStatus !== "available") return this.factories;
    return this.factories.filter((factory) =>
      this.supportsFactory(factory.id) || this.products.some((product) => {
        const key = productAllocationKey(this.slotType, factory.id, product.id);
        return this.fState.getBaseline(key).value > 0 ||
          this.fState.isModified(key) || this.fState.isStaged(key) || this.hasPendingKey(key);
      })
    );
  }

  public supportsFactory(factoryId: string): boolean {
    return this.supportStatus === "available" && !!this.supportedFactoryIds[factoryId];
  }

  public getSubjectAnnotation(subject: Factory, orientation: AllocationOrientation): string {
    return orientation === "factory" ? this.getFactoryWarning(subject.id) : "";
  }

  public getDetailWarning(subjectId: string, detailId: string, orientation: AllocationOrientation): string {
    return this.getFactoryWarning(orientation === "item" ? detailId : subjectId);
  }

  private getFactoryWarning(factoryId: string): string {
    if (this.supportStatus !== "available" || this.supportsFactory(factoryId)) return "";
    return `Does not support ${this.slotType} — reduce or clear existing allocations`;
  }

  get supportedFactoryIds(): Dictionary<boolean> {
    return this.fSupportSnapshot.reduce((index, support) => {
      if (support.slotType === this.slotType) index[support.factory] = true;
      return index;
    }, {} as Dictionary<boolean>);
  }

  get products(): Product[] {
    return this.fPlushieDataStore.activeProductsList;
  }

  get isLoading(): boolean {
    return this.fIsLoading;
  }

  get supportContextKey(): string {
    const user = this.fUserProvider.getUser();
    return JSON.stringify([
      user && user.id,
      user && user.hasPermissionForResource(Resource.FACTORY_PHASE_SUPPORT_VIEW),
      this.slotType,
      this.factories.map((factory) => factory.id).sort(),
    ]);
  }
  get supportStatus(): string {
    return this.fSupportKey === this.supportContextKey ? this.fSupportStatus : "unknown";
  }
  get supportErrors(): string[] { return this.fSupportErrors; }
  get inputResetToken(): number { return this.fInputResetToken; }

  get isSaving(): boolean {
    return this.fIsSaving;
  }

  get isSaved(): boolean {
    return this.fIsSaved;
  }

  get loadErrors(): string[] {
    return this.fLoadErrors;
  }

  get saveErrors(): string[] {
    return this.fSaveErrors;
  }

  get isDirty(): boolean {
    return this.fState.isDirty || this.hasPendingInputs;
  }

  get hasPendingInputs(): boolean { return Object.keys(this.fPendingInputs).length > 0; }

  get invalidPercentageMessage(): string {
    return invalidPercentageMessage;
  }

  get productSummaries(): Dictionary<ProductAllocationSummary> {
    const summaries: Dictionary<ProductAllocationSummary> = {};
    const keys = new Set(this.fState.baselineKeys.concat(this.fState.dirtyKeys));
    keys.forEach((key) => {
      const [slot, , productId] = key.split(":");
      if (slot !== this.slotType || !productId) return;
      const value = this.fState.getEffectiveValue(key);
      const summary = summaries[productId] || { total: 0, hasInvalidPercentage: false };
      summary.total += Number(value);
      summary.hasInvalidPercentage = summary.hasInvalidPercentage || !isValidAllocationPercent(value);
      summaries[productId] = summary;
    });
    return summaries;
  }

  get productsWithInvalidPercentages(): Product[] {
    return this.products.filter(
      (product) => this.getProductSummary(product.id).hasInvalidPercentage
    );
  }

  get productsWithInvalidTotals(): Product[] {
    return this.products.filter((product) => {
      const summary = this.getProductSummary(product.id);
      return (
        !summary.hasInvalidPercentage && !isValidProductTotal(summary.total)
      );
    });
  }

  get invalidProducts(): Product[] {
    return this.products.filter((product) => !this.isProductValid(product.id));
  }

  get hasInvalidDraftPercentages(): boolean {
    return this.fState.dirtyKeys.some(
      (key) => !isValidAllocationPercent(this.fState.drafts[key])
    );
  }

  get hasInvalidPercentages(): boolean {
    return (
      this.hasInvalidDraftPercentages ||
      this.productsWithInvalidPercentages.length > 0
    );
  }

  get hasValidationErrors(): boolean {
    return this.hasInvalidPercentages || this.invalidProducts.length > 0 || this.hasInvalidSupportDrafts;
  }

  get hasInvalidSupportDrafts(): boolean {
    if (this.supportStatus !== "available") return false;
    return this.fState.dirtyKeys.some((key) => {
      const [, factoryId] = key.split(":");
      if (this.supportsFactory(factoryId)) return false;
      const baseline = this.fState.getBaseline(key);
      return !baseline.isConfigured || baseline.value <= 0 ||
        this.fState.getEffectiveValue(key) > baseline.value;
    });
  }

  get showMobileValidation(): boolean {
    return this.fShowMobileValidation;
  }

  get canSave(): boolean {
    return (
      this.editable &&
      this.supportStatus === "available" &&
      this.isDirty &&
      !this.hasPendingInputs &&
      !this.isSaving &&
      !this.hasValidationErrors
    );
  }

  get canDiscard(): boolean {
    return this.isDirty || this.fState.hasStagedDetails;
  }

  public getProductTotal(productId: string): number {
    return this.getProductSummary(productId).total;
  }

  public isProductValid(productId: string): boolean {
    const summary = this.getProductSummary(productId);
    return !summary.hasInvalidPercentage && isValidProductTotal(summary.total);
  }

  public getPairValue(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): number {
    return this.fState.getEffectiveValue(
      this.getPairKey(subjectId, detailId, orientation)
    );
  }

  public remainingAllocation(subjectId: string, detailId: string, orientation: AllocationOrientation): number {
    const productId = orientation === "item" ? subjectId : detailId;
    const current = this.getPairValue(subjectId, detailId, orientation);
    return Math.max(0, Math.min(100, 100 - (this.getProductTotal(productId) - current)));
  }

  public canUseQuickAction(subjectId: string, detailId: string, orientation: AllocationOrientation, value: number): boolean {
    const key = this.getPairKey(subjectId, detailId, orientation);
    const current = this.fState.getEffectiveValue(key);
    const factoryId = orientation === "item" ? detailId : subjectId;
    return this.editable && !this.isSaving && this.supportStatus === "available" &&
      !this.hasPendingKey(key) && isValidAllocationPercent(value) && value !== current &&
      (this.supportsFactory(factoryId) || value <= current);
  }

  public setQuickAllocation(subjectId: string, detailId: string, orientation: AllocationOrientation, value: number): void {
    if (this.canUseQuickAction(subjectId, detailId, orientation, value)) {
      this.onValueInput(subjectId, detailId, orientation, value);
    }
  }

  public isPairConfigured(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): boolean {
    return this.fState.isConfigured(
      this.getPairKey(subjectId, detailId, orientation)
    );
  }

  public isPairModified(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): boolean {
    return this.fState.isModified(
      this.getPairKey(subjectId, detailId, orientation)
    );
  }

  public getDetailStatus(
    subject: Product,
    detail: Factory,
    orientation: AllocationOrientation
  ): AllocationDetailStatus {
    if (!this.isPairConfigured(subject.id, detail.id, orientation)) {
      return "unconfigured";
    }

    return this.getPairValue(subject.id, detail.id, orientation) > 0
      ? "enabled"
      : "disabled";
  }

  public canAddDetail(
    subject: Product,
    detail: Factory,
    orientation: AllocationOrientation
  ): boolean {
    const key = this.getPairKey(subject.id, detail.id, orientation);
    return (
      this.supportStatus === "available" &&
      this.supportsFactory(orientation === "item" ? detail.id : subject.id) &&
      this.fState.getEffectiveValue(key) === 0 &&
      !this.fState.isStaged(key) &&
      !this.fState.isModified(key)
    );
  }

  public isStagedDetail(
    subject: Product,
    detail: Factory,
    orientation: AllocationOrientation
  ): boolean {
    const key = this.getPairKey(subject.id, detail.id, orientation);
    return this.fState.isStaged(key);
  }

  public isModifiedDetail(
    subject: Product,
    detail: Factory,
    orientation: AllocationOrientation
  ): boolean {
    return this.isPairModified(subject.id, detail.id, orientation) ||
      this.hasPendingKey(this.getPairKey(subject.id, detail.id, orientation));
  }

  private hasPendingKey(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.fPendingInputs, key);
  }

  public isBufferedPair(subjectId: string, detailId: string, orientation: AllocationOrientation): boolean {
    const key = this.getPairKey(subjectId, detailId, orientation);
    const factoryId = orientation === "item" ? detailId : subjectId;
    return this.hasPendingKey(key) || (this.supportStatus === "available" && !this.supportsFactory(factoryId));
  }

  public pendingValue(subjectId: string, detailId: string, orientation: AllocationOrientation): string | undefined {
    return this.fPendingInputs[this.getPairKey(subjectId, detailId, orientation)];
  }

  public setPendingValue(subjectId: string, detailId: string, orientation: AllocationOrientation, text: string): void {
    if (!this.editable || this.isSaving || this.supportStatus !== "available") return;
    Vue.set(this.fPendingInputs, this.getPairKey(subjectId, detailId, orientation), text);
    this.fIsSaved = false;
    this.fSaveErrors = [];
  }

  public commitPendingValue(subjectId: string, detailId: string, orientation: AllocationOrientation): void {
    const key = this.getPairKey(subjectId, detailId, orientation);
    if (!this.hasPendingKey(key) || !this.editable || this.isSaving || this.supportStatus !== "available") return;
    const text = this.fPendingInputs[key].trim();
    const value = text === "" ? NaN : Math.max(0, Number(text));
    const factoryId = orientation === "item" ? detailId : subjectId;
    const baseline = this.fState.getBaseline(key);
    if (!isValidAllocationPercent(value) || (!this.supportsFactory(factoryId) &&
        (!baseline.isConfigured || baseline.value <= 0 || value > baseline.value || value > this.fState.getEffectiveValue(key)))) {
      this.fSaveErrors = ["Enter a whole percentage that only reduces or clears the existing allocation."];
      return;
    }
    this.onValueInput(subjectId, detailId, orientation, value);
    Vue.delete(this.fPendingInputs, key);
  }

  public addCounterpart(
    subject: Product,
    detail: Factory,
    orientation: AllocationOrientation
  ): void {
    if (
      !this.editable ||
      this.supportStatus !== "available" ||
      this.isSaving ||
      !this.canAddDetail(subject, detail, orientation)
    ) {
      return;
    }
    this.fState.stage(this.getPairKey(subject.id, detail.id, orientation));
    this.fIsSaved = false;
    this.fSaveErrors = [];
    this.fShowMobileValidation = false;
  }

  public onValueInput(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation,
    value: number
  ): void {
    if (!this.editable || this.isSaving || this.supportStatus !== "available") return;
    const key = this.getPairKey(subjectId, detailId, orientation);
    const factoryId = orientation === "item" ? detailId : subjectId;
    if (!this.supportsFactory(factoryId)) {
      const baseline = this.fState.getBaseline(key);
      if (!baseline.isConfigured || baseline.value <= 0 ||
          !isValidAllocationPercent(value) || value > baseline.value ||
          value > this.fState.getEffectiveValue(key)) {
        this.fSaveErrors = ["Unsupported factories only allow reducing or clearing existing allocations."];
        this.fInputResetToken++;
        return;
      }
    }
    this.fState.setValue(
      key,
      value
    );
    this.fIsSaved = false;
    this.fSaveErrors = [];
    this.fShowMobileValidation = false;
  }

  public toggleMobileValidation(): void {
    this.fShowMobileValidation = !this.fShowMobileValidation;
  }

  public async save(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    const payload = materializeProductAllocations(
      this.slotType,
      this.products,
      this.factories,
      this.fState,
      this.fIdGenerator,
      (factoryId) => this.supportsFactory(factoryId)
    );
    if (
      payload.some(
        (allocation) =>
          !isValidAllocationPercent(allocation.distributionPercent)
      )
    ) {
      this.fSaveErrors = [invalidPercentageMessage];
      return;
    }

    this.fIsSaving = true;
    this.fIsSaved = false;
    this.fSaveErrors = [];
    try {
      const saved = await this.fFactoryDataStore.saveProductAllocations(
        payload
      );
      this.fState.replaceBaseline(
        createProductAllocationBaseline(saved, this.slotType)
      );
      this.fIsSaved = true;
    } catch (error) {
      this.fSaveErrors = this.fErrorConverterService.describeError(error);
    } finally {
      this.fIsSaving = false;
    }
  }

  public discardChanges(): void {
    if (this.isSaving) {
      return;
    }
    this.fState.clearDrafts();
    this.fPendingInputs = {};
    this.fInputResetToken++;
    this.fSaveErrors = [];
    this.fIsSaved = false;
    this.fShowMobileValidation = false;
  }

  public async loadData(): Promise<void> {
    this.fIsLoading = true;
    this.fLoadErrors = [];
    this.fShowMobileValidation = false;
    try {
      const [allocations] = await Promise.all([
        this.fFactoryDataStore.loadProductAllocations([this.slotType]),
        this.fFactoryDataStore.loadFactories(),
        this.fPlushieDataStore.loadProducts(),
      ]);
      if (this.fDestroyed) return;
      this.fState = new SparseAllocationDraft<number>(
        createProductAllocationBaseline(allocations, this.slotType),
        0
      );
      void this.loadSupport();
    } catch (error) {
      if (!this.fDestroyed) this.fLoadErrors = this.fErrorConverterService.describeError(error);
    } finally {
      if (!this.fDestroyed) this.fIsLoading = false;
    }
  }

  public async loadSupport(): Promise<void> {
    const generation = ++this.fSupportGeneration;
    const key = this.supportContextKey;
    this.fSupportKey = key;
    const user = this.fUserProvider.getUser();
    this.fSupportErrors = [];
    if (!user || !user.hasPermissionForResource(Resource.FACTORY_PHASE_SUPPORT_VIEW)) {
      this.fSupportStatus = "permission";
      this.fSupportErrors = ["Factory phase support read access is required to edit allocations."];
      return;
    }
    this.fSupportStatus = "loading";
    try {
      const snapshot = await this.fFactoryDataStore.loadCompleteFactoryPhaseSupports({
        factoryIds: this.factories.map((factory) => factory.id),
        isCurrent: () => generation === this.fSupportGeneration && key === this.supportContextKey,
      });
      if (generation === this.fSupportGeneration && key === this.supportContextKey) {
        this.fSupportSnapshot = snapshot;
        this.fSupportStatus = "available";
      }
    } catch (error) {
      if (generation === this.fSupportGeneration && key === this.supportContextKey) {
        this.fSupportStatus = "error";
        this.fSupportErrors = this.fErrorConverterService.describeError(error);
      }
    }
  }

  @Watch("supportContextKey")
  private onSupportContextChanged(): void {
    if (!this.isLoading) void this.loadSupport();
  }

  protected created(): void {
    this.fRemoveDraftGuard = protectAllocationDraft(
      this.$router,
      () => this.isDirty,
      () => this.isSaving,
      () => this.$auth.check()
    );
    void this.loadData();
  }

  protected beforeDestroy(): void {
    this.fDestroyed = true;
    this.fSupportGeneration++;
    this.fRemoveDraftGuard();
  }

  private getProductSummary(productId: string): ProductAllocationSummary {
    return this.productSummaries[productId] || { total: 0, hasInvalidPercentage: false };
  }

  private getPairKey(
    subjectId: string,
    detailId: string,
    orientation: AllocationOrientation
  ): string {
    const productId = orientation === "item" ? subjectId : detailId;
    const factoryId = orientation === "item" ? detailId : subjectId;
    return productAllocationKey(this.slotType, factoryId, productId);
  }
}
